"use client";

import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store";
import { fetchMe as fetchMeThunk } from "@/store/modules/profile/thunks";
import { useAuth } from "@/context/AuthContext";

export interface UseMeOptions {
  refreshInterval?: number; // em segundos
}

export interface UseMeResult {
  user: any | null;
  loading: boolean;
  error: any | null;
  refresh: () => Promise<void>;
}

export const useMe = (options?: UseMeOptions): UseMeResult => {
  const dispatch = useDispatch<AppDispatch>();
  const profileState = useSelector((s: any) => s.profile);
  const { token, isLoading: authLoading } = useAuth();

  const fetchMe = useCallback(
    async (noCache = false) => {
      try {
        await dispatch(fetchMeThunk({ noCache } as any));
      } catch (e) {
        // noop - errors handled in slice
      }
    },
    [dispatch],
  );

  useEffect(() => {
    // Do not attempt to fetch /me until auth token is set (prevents 401 caused by race)
    if (authLoading) return;

    if (!token) {
      // No token available — don't call backend without Authorization
      return;
    }

    // Avoid redundant fetches: skip only when we already have a loaded user or a fetch is in progress
    // This ensures that if profile was marked fetched but user is null (e.g., earlier 404/401),
    // we still attempt to fetch again when token becomes available.
    if (profileState?.loading) {
      // fetch in progress — don't start another
    } else if (profileState?.fetched && profileState?.user) {
      // already fetched and have a user -> nothing to do
    } else {
      // Otherwise, fetch initial profile
      fetchMe(false);
    }

    // Optional periodic refresh — will run regardless of 'fetched' state if interval configured
    let intervalId: number | undefined;
    if (options?.refreshInterval && options.refreshInterval > 0) {
      intervalId = window.setInterval(
        () => fetchMe(false),
        options.refreshInterval * 1000,
      );
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    fetchMe,
    options?.refreshInterval,
    token,
    authLoading,
    profileState?.fetched,
    profileState?.loading,
  ]);

  return {
    user: profileState?.user ?? null,
    loading: profileState?.loading ?? false,
    error: profileState?.error ?? null,
    refresh: () => fetchMe(true),
  };
};

export default useMe;
